import $ from "jquery";
import { createCalendar, serializeSelectedSet } from "./core.js";
import { createCalendarToggleControl } from "./filters.js";

/**
 * SFC-specific calendar entry point.
 *
 * Wires the generic calendar core to the SFC page: the calendar toggle pills
 * (#calendar-filters) via the built-in control, the category multi-select
 * (#category-select, a Select2 widget) registered as a filter control, and the
 * Slovenian locale. The category->calendar coupling flows one way: toggling a
 * calendar hides the categories of unselected calendars from the Select2 (see
 * syncCategoryOptions) and, through the category predicate, their events.
 */
export function initCalendar(el) {
  var categorySelect = document.getElementById("category-select");
  var calendarFilters = document.getElementById("calendar-filters");

  // Which categories the user has selected
  var activeFilters = new Set();
  // All category IDs (for default detection)
  var allCategoryIds = new Set();
  // category ID -> calendar ID lookup
  var catToCalendar = {};
  // Every category <option>, kept in original order so we can detach/reattach
  // options as their calendar is toggled off/on without losing them.
  var allCategoryOptions = [];

  // Recompute activeFilters from the select's currently-attached selection.
  // Detached options (calendar toggled off) drop out automatically.
  function readActiveFilters() {
    activeFilters.clear();
    if (!categorySelect) return;
    Array.from(categorySelect.selectedOptions).forEach(function (option) {
      activeFilters.add(option.value);
    });
  }

  if (categorySelect) {
    allCategoryOptions = Array.prototype.slice.call(categorySelect.options);
    allCategoryOptions.forEach(function (option) {
      allCategoryIds.add(option.value);
      var calId = option.getAttribute("data-calendar-id");
      if (calId) {
        catToCalendar[option.value] = calId;
      }
    });
    readActiveFilters();
  }

  var controllerRef = null;

  function isCategoryVisible(catId) {
    var calId = catToCalendar[catId];
    if (!calId) return true;
    var state = controllerRef && controllerRef.getFilterState("calendars");
    return !state || state.active.has(calId);
  }

  // Rebuild the category Select2 to only show categories whose calendar is
  // currently selected. Detached options keep their `selected` state, so
  // re-enabling a calendar restores its previously chosen categories. Because
  // this constrains activeFilters to visible categories, the event predicate
  // only needs to test activeFilters membership.
  function syncCategoryOptions() {
    if (!categorySelect) return;
    categorySelect.replaceChildren();
    allCategoryOptions.forEach(function (option) {
      if (isCategoryVisible(option.value)) {
        categorySelect.appendChild(option);
      }
    });
    readActiveFilters();
    $(categorySelect).trigger("change.select2");
  }

  var categoryControl = {
    key: "categories",
    predicate: function (e) {
      return activeFilters.has(String(e.extendedProps.categoryId));
    },
    serialize: function () {
      return serializeSelectedSet(activeFilters, allCategoryIds);
    },
    getState: function () {
      return { active: activeFilters, all: allCategoryIds };
    },
    init: function (ctx) {
      controllerRef = ctx.controller;
      if (!categorySelect) return;
      // Select2 fires jQuery change events
      $(categorySelect).on("change", function () {
        readActiveFilters();
        ctx.triggerChange("categories");
      });
      // Keep the category Select2 in sync with the calendar selection.
      ctx.controller.onFilterChange(function (changedKey) {
        if (changedKey === "calendars") {
          syncCategoryOptions();
        }
      });
      syncCategoryOptions();
    },
  };

  var filters = [];
  if (calendarFilters) {
    filters.push(
      createCalendarToggleControl(el, { containerId: "calendar-filters" }),
    );
  }
  filters.push(categoryControl);

  return createCalendar(el, { filters: filters });
}
